import React, { useEffect, useState } from "react";
import {
  Users, Store, CreditCard, TrendingUp, Wallet, Gift, Banknote, ShieldAlert,
  TrendingDown, RefreshCw, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/apiClient";

const money = (m) => m?.display ?? "—";

export default function DashboardHome({ onSettle }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const settle = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/settle"); toast.success(`Settled ${data.settled} entries`); load(); onSettle?.(); }
    catch { toast.error("Settlement failed"); } finally { setBusy(false); }
  };

  const bh = d?.business_health;

  return (
    <div className="space-y-8" data-testid="admin-dashboard-home">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold">Operations overview</h1>
          <p className="text-sm text-slate-400">Real-time business snapshot · all figures exact (paise-based)</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="admin-run-settlement" onClick={settle} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Run settlement
          </button>
          <button data-testid="admin-refresh-dashboard" onClick={load} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-sm hover:border-emerald-500/50"><RefreshCw className="h-4 w-4" /> Refresh</button>
        </div>
      </div>

      {/* Primary KPI groups */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Group icon={Users} title="Customers" accent="text-sky-400" testid="kpi-customers" rows={[
          ["Total", d?.customers?.total], ["Active", d?.customers?.active],
          ["New today", d?.customers?.new_today], ["New this month", d?.customers?.new_month],
        ]} />
        <Group icon={Store} title="Merchants" accent="text-violet-400" testid="kpi-merchants" rows={[
          ["Total", d?.merchants?.total], ["Active", d?.merchants?.active],
          ["Pending KYC", d?.merchants?.pending], ["Suspended", d?.merchants?.suspended],
        ]} />
        <Group icon={CreditCard} title="Subscriptions" accent="text-amber-400" testid="kpi-subscriptions" rows={[
          ["Active", d?.subscriptions?.active], ["New this month", d?.subscriptions?.new_month],
          ["Cancelled", d?.subscriptions?.cancelled], ["Refunded", d?.subscriptions?.refunded],
        ]} />
        <Group icon={TrendingUp} title="Revenue" accent="text-emerald-400" testid="kpi-revenue" money rows={[
          ["Gross", money(d?.revenue?.gross)], ["GST", money(d?.revenue?.gst)], ["Net", money(d?.revenue?.net)],
        ]} />
        <Group icon={Wallet} title="Central campaign budget" accent="text-emerald-400" testid="kpi-budget" money rows={[
          ["Funded", money(d?.budget?.funded)], ["Used (cashback)", money(d?.budget?.used)],
          ["Reversed", money(d?.budget?.reversed)], ["Remaining", money(d?.budget?.remaining)],
        ]} />
        <Group icon={Gift} title="Cashback" accent="text-pink-400" testid="kpi-cashback" money rows={[
          ["Issued", money(d?.cashback?.issued)], ["Pending", money(d?.cashback?.pending)],
          ["Available", money(d?.cashback?.available)], ["Reversed", money(d?.cashback?.reversed)],
          ["₹0 draws", d?.cashback?.zero_count],
        ]} />
        <Group icon={Banknote} title="Redemptions" accent="text-cyan-400" testid="kpi-redemptions" rows={[
          ["Pending", d?.redemptions?.pending], ["Processing", d?.redemptions?.processing],
          ["Completed", d?.redemptions?.completed], ["Failed", d?.redemptions?.failed],
          ["Rejected", d?.redemptions?.rejected],
        ]} />
        <Group icon={ShieldAlert} title="Fraud / Risk" accent="text-rose-400" testid="kpi-fraud" rows={[
          ["Open flags", d?.fraud?.open_flags], ["High-risk customers", d?.fraud?.high_risk_customers],
          ["High-risk merchants", d?.fraud?.high_risk_merchants], ["Under review", d?.fraud?.under_review],
        ]} />
        <Group icon={TrendingDown} title="Loss / Exposure" accent="text-orange-400" testid="kpi-loss" money rows={[
          ["Cashback cost", money(d?.loss?.cashback_cost)], ["Payment fees", money(d?.loss?.payment_fees)],
          ["Refunds", money(d?.loss?.refunds)], ["Pending exposure", money(d?.loss?.exposure)],
        ]} />
      </div>

      {/* Business Health */}
      <div className="ledger-card rounded-2xl p-6" data-testid="admin-business-health">
        <h2 className="font-heading text-lg font-bold">Business health summary</h2>
        <p className="mt-1 text-xs text-slate-500">Net result = Net revenue − cashback cost − payment fees − refunds. Values are accounting figures, not audited profit.</p>
        <div className="mt-5 space-y-1.5">
          <HealthRow label="Gross revenue" value={money(bh?.gross_revenue)} />
          <HealthRow label="GST" value={`− ${money(bh?.gst)}`} muted />
          <HealthRow label="Net revenue" value={money(bh?.net_revenue)} strong />
          <HealthRow label="Cashback cost" value={`− ${money(bh?.cashback_cost)}`} negative />
          <HealthRow label="Payment fees" value={`− ${money(bh?.payment_fees)}`} negative />
          <HealthRow label="Refunds" value={`− ${money(bh?.refunds)}`} negative />
          <div className="my-2 border-t border-slate-800" />
          <HealthRow label="Net result" value={money(bh?.net_result)} result={(bh?.net_result?.paise ?? 0) >= 0} />
        </div>
      </div>
    </div>
  );
}

function Group({ icon: Icon, title, accent, rows, money: isMoney, testid }) {
  return (
    <div className="ledger-card rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${accent}`} />
        <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{title}</p>
      </div>
      <div className="mt-3 space-y-2">
        {rows.map(([label, val], i) => (
          <div key={i} className="flex items-center justify-between">
            <span className="text-sm text-slate-400">{label}</span>
            <span className={`font-heading text-sm font-bold ${i === 0 ? accent : "text-slate-100"}`}>
              {val ?? (isMoney ? "—" : 0)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function HealthRow({ label, value, muted, strong, negative, result }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-sm ${strong || result ? "font-semibold text-slate-200" : "text-slate-400"}`}>{label}</span>
      <span className={`font-mono text-sm ${result !== undefined ? (result ? "text-emerald-400 font-bold text-base" : "text-rose-400 font-bold text-base") : negative ? "text-rose-300" : muted ? "text-slate-500" : strong ? "text-emerald-400 font-semibold" : "text-slate-100"}`}>
        {value}
      </span>
    </div>
  );
}
